import { motion } from "framer-motion";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiBookmark,
  FiCalendar,
  FiCheckCircle,
  FiChevronRight,
  FiExternalLink,
  FiGlobe,
  FiInfo,
  FiMessageSquare,
  FiShare2,
  FiTag,
  FiThumbsDown,
  FiThumbsUp,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCategoryTopSites, websites } from "../../data/fakeData";
import SitePreview from "../UI/SitePreview";
import StarRating from "../UI/StarRating";
import ReviewForm from "./ReviewForm";
import ReviewSection from "./ReviewSection";

/* ─── Rating Bar ───────────────────────────── */
const RatingBar = ({ stars, pct, delay = 0 }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs font-bold text-slate-600 w-10 flex-shrink-0">
      {stars} star
    </span>
    <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${pct}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay }}
        className="h-full bg-indigo-500 rounded-full"
      />
    </div>
    <span className="text-xs font-bold text-slate-600 w-10 text-right flex-shrink-0">
      {pct}%
    </span>
  </div>
);

/* ─── Info Row ──────────────────────────────── */
const InfoRow = ({ icon: Icon, label, value, isLink, href }) => (
  <div className="flex items-center justify-between py-3 border-b-2 border-slate-100 last:border-0">
    <span className="text-sm font-semibold text-slate-500 flex items-center gap-2 flex-shrink-0">
      <Icon size={14} className="text-slate-400" />
      {label}
    </span>
    {isLink ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm font-bold text-indigo-600 hover:text-indigo-700 hover:underline truncate max-w-[150px] text-right transition-colors"
      >
        {value}
      </a>
    ) : (
      <span className="text-sm font-bold text-slate-900 text-right">
        {value}
      </span>
    )}
  </div>
);

/* ═══════════════════════════════════════════ */
const WebsiteDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const site = websites.find((s) => s.id === parseInt(id));

  if (!site) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <FiInfo size={24} className="text-slate-400" />
          </div>
          <h2 className="text-xl font-extrabold text-slate-900 mb-2">
            Website not found
          </h2>
          <p className="text-slate-600 mb-6">
            The page you're looking for doesn't exist.
          </p>
          <Link to="/websites" className="btn-primary">
            <FiArrowLeft size={15} /> Back to directory
          </Link>
        </div>
      </div>
    );
  }

  const related = getCategoryTopSites(site.categorySlug, 6)
    .filter((s) => s.id !== site.id)
    .slice(0, 4);

  const ratingDist = [
    { stars: 5, pct: 65 },
    { stars: 4, pct: 20 },
    { stars: 3, pct: 10 },
    { stars: 2, pct: 3 },
    { stars: 1, pct: 2 },
  ];

  const handleSave = () => {
    if (!user) {
      toast.error("Sign in to bookmark websites");
      return;
    }
    setSaved(!saved);
    toast.success(saved ? "Removed from bookmarks" : "Saved to bookmarks");
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  return (
    <div className="section-tinted min-h-screen">
      <div className="container-main py-6 lg:py-10">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-1.5 text-sm mb-6 flex-wrap"
        >
          <Link
            to="/"
            className="font-semibold text-slate-500 hover:text-indigo-600 transition-colors"
          >
            Home
          </Link>
          <FiChevronRight size={11} className="text-slate-400" />
          <Link
            to={`/category/${site.categorySlug}`}
            className="font-semibold text-slate-500 hover:text-indigo-600 transition-colors"
          >
            {site.category}
          </Link>
          <FiChevronRight size={11} className="text-slate-400" />
          <span className="font-bold text-slate-900">{site.name}</span>
        </motion.nav>

        {/* Layout */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-start">
          {/* ════ LEFT ════ */}
          <div className="lg:col-span-2 space-y-5">
            {/* ── Hero Card ── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card overflow-hidden"
            >
              {/* Screenshot */}
              <div className="relative">
                <SitePreview
                  url={site.url}
                  alt={site.name}
                  className="w-full h-56 sm:h-64 lg:h-80"
                />
                {/* Dark gradient overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent z-10 pointer-events-none" />

                {/* Overlay content */}
                <div className="absolute bottom-0 left-0 right-0 z-20 p-5 sm:p-6">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2.5 mb-2">
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-white leading-tight">
                          {site.name}
                        </h1>
                        {site.verified && (
                          <FiCheckCircle
                            size={18}
                            className="text-emerald-400 flex-shrink-0"
                          />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="badge bg-white/20 text-white border-transparent backdrop-blur-sm">
                          {site.category}
                        </span>
                        {site.featured && (
                          <span className="badge bg-amber-400 text-amber-950 border-transparent font-bold uppercase text-[9px] tracking-wider">
                            Featured
                          </span>
                        )}
                        {site.verified && (
                          <span className="badge bg-emerald-500/20 text-emerald-300 border-transparent">
                            Verified
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Rating bubble */}
                    <div className="flex-shrink-0 bg-white/15 backdrop-blur-md rounded-xl px-3 py-2 text-center border border-white/10">
                      <p className="text-xl font-extrabold text-white leading-none">
                        {site.rating}
                      </p>
                      <p className="text-[9px] text-white/60 mt-1 font-bold">
                        {site.totalReviews.toLocaleString()} reviews
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5 bg-slate-50 border-t-2 border-slate-100">
                <div className="flex items-center gap-3 flex-wrap">
                  <StarRating rating={site.rating} size="sm" />
                  <span className="text-lg font-extrabold text-slate-900">
                    {site.rating}
                  </span>
                  <span className="text-sm font-semibold text-slate-500">
                    ({site.totalReviews.toLocaleString()} reviews)
                  </span>
                  <span className="hidden sm:block text-slate-300">·</span>
                  <span className="hidden sm:block text-sm font-semibold text-slate-500">
                    {site.monthlyVisits}/mo
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSave}
                    title={saved ? "Remove bookmark" : "Save"}
                    className={`btn-secondary !py-2 !px-3 ${
                      saved
                        ? "!bg-indigo-50 !text-indigo-600 !border-indigo-200"
                        : ""
                    }`}
                  >
                    <FiBookmark size={14} />
                  </button>
                  <button
                    onClick={handleShare}
                    title="Share"
                    className="btn-secondary !py-2 !px-3"
                  >
                    <FiShare2 size={14} />
                  </button>
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary !py-2"
                  >
                    <FiExternalLink size={14} /> Visit Website
                  </a>
                </div>
              </div>
            </motion.div>

            {/* ── Tabs ── */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
              {["overview", "reviews"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2 rounded-lg text-sm font-bold capitalize transition-all ${
                    activeTab === tab
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {tab}
                  {tab === "reviews" && site.reviews?.length > 0 && (
                    <span className="ml-1.5 bg-indigo-100 text-indigo-600 text-[10px] font-extrabold px-1.5 py-0.5 rounded-full">
                      {site.reviews.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* ══ OVERVIEW ══ */}
            {activeTab === "overview" && (
              <div className="space-y-5">
                {/* About */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card p-5 sm:p-6"
                >
                  <h2 className="text-lg font-extrabold text-slate-900 mb-3">
                    About {site.name}
                  </h2>
                  <p className="text-[15px] text-slate-700 leading-relaxed mb-5">
                    {site.longDescription}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {site.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1.5 badge-slate text-[11px]"
                      >
                        <FiTag size={9} /> {tag}
                      </span>
                    ))}
                  </div>
                </motion.div>

                {/* Pros & Cons */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="card p-5"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <FiThumbsUp size={13} className="text-emerald-600" />
                      </div>
                      <h3 className="text-sm font-extrabold text-slate-900">
                        Pros
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {site.pros.map((pro, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed"
                        >
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 }}
                    className="card p-5"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
                        <FiThumbsDown size={13} className="text-red-600" />
                      </div>
                      <h3 className="text-sm font-extrabold text-slate-900">
                        Cons
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {site.cons.map((con, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed"
                        >
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </div>

                {/* Rating Breakdown */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="card p-5 sm:p-6"
                >
                  <h2 className="text-lg font-extrabold text-slate-900 mb-6">
                    Rating Breakdown
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                    <div className="flex-shrink-0 text-center sm:border-r-2 sm:border-slate-100 sm:pr-8">
                      <div className="text-5xl font-extrabold text-slate-900 font-display leading-none mb-2">
                        {site.rating}
                      </div>
                      <StarRating rating={site.rating} size="sm" />
                      <p className="text-xs font-bold text-slate-500 mt-2">
                        out of 5.0
                      </p>
                      <p className="text-xs font-semibold text-slate-400 mt-1">
                        {site.totalReviews.toLocaleString()} reviews
                      </p>
                    </div>
                    <div className="flex-1 space-y-3">
                      {ratingDist.map((item, i) => (
                        <RatingBar
                          key={item.stars}
                          stars={item.stars}
                          pct={item.pct}
                          delay={i * 0.08}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>

                {/* Review CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 }}
                  className="card p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-2 border-indigo-100 bg-indigo-50/40"
                >
                  <div>
                    <p className="text-sm font-extrabold text-slate-900">
                      Tried {site.name}?
                    </p>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Share your experience and help others decide.
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab("reviews")}
                    className="btn-primary flex-shrink-0"
                  >
                    Write a Review
                  </button>
                </motion.div>
              </div>
            )}

            {/* ══ REVIEWS ══ */}
            {activeTab === "reviews" && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-5"
              >
                <ReviewForm siteId={site.id} />
                <ReviewSection reviews={site.reviews} />
              </motion.div>
            )}
          </div>

          {/* ════ RIGHT — Sidebar ════ */}
          <div>
            <div className="lg:sticky lg:top-[4.5rem] space-y-5">
              {/* Website Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="card p-5"
              >
                <div className="flex items-center gap-3 mb-4 pb-4 border-b-2 border-slate-100">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <FiGlobe size={18} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900">
                      Website Info
                    </h3>
                    <p className="text-xs font-semibold text-slate-500">
                      Quick facts about {site.name}
                    </p>
                  </div>
                </div>

                <InfoRow
                  icon={FiGlobe}
                  label="Website"
                  value={site.url.replace("https://", "").replace("www.", "")}
                  isLink
                  href={site.url}
                />
                <InfoRow
                  icon={FiCalendar}
                  label="Founded"
                  value={site.founded}
                />
                <InfoRow
                  icon={FiUsers}
                  label="Monthly Visits"
                  value={site.monthlyVisits}
                />
                <InfoRow
                  icon={FiMessageSquare}
                  label="Reviews"
                  value={site.totalReviews.toLocaleString()}
                />
                <InfoRow
                  icon={FiTrendingUp}
                  label="Category Rank"
                  value={`#${site.rank} in ${site.category}`}
                />

                <div className="mt-4 pt-4 border-t-2 border-slate-100 space-y-2.5">
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full !py-2.5"
                  >
                    <FiExternalLink size={14} /> Visit {site.name}
                  </a>
                  <button
                    onClick={handleSave}
                    className={`btn-secondary w-full !py-2.5 ${
                      saved
                        ? "!bg-indigo-50 !text-indigo-600 !border-indigo-200"
                        : ""
                    }`}
                  >
                    <FiBookmark size={14} />
                    {saved ? "Remove Bookmark" : "Save to Bookmarks"}
                  </button>
                  <button
                    onClick={handleShare}
                    className="btn-secondary w-full !py-2.5"
                  >
                    <FiShare2 size={14} /> Share This Page
                  </button>
                </div>
              </motion.div>

              {/* Screenshot Preview */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="card overflow-hidden"
              >
                <div className="relative group">
                  <SitePreview
                    url={site.url}
                    alt={site.name}
                    className="w-full h-36"
                    imgClassName="group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20">
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/90 backdrop-blur-sm text-slate-800 font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-1.5 shadow-lg hover:bg-white transition-colors"
                    >
                      <FiExternalLink size={12} /> Open Website
                    </a>
                  </div>
                </div>
                <div className="px-4 py-2.5 flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-500">Preview</p>
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors"
                  >
                    Open <FiExternalLink size={10} />
                  </a>
                </div>
              </motion.div>

              {/* Related Sites */}
              {related.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="card p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-extrabold text-slate-900">
                      More in {site.category}
                    </h3>
                    <Link
                      to={`/category/${site.categorySlug}`}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      View all →
                    </Link>
                  </div>
                  <div className="space-y-2">
                    {related.map((r) => (
                      <Link
                        key={r.id}
                        to={`/website/${r.id}`}
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 group transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 ring-2 ring-slate-100">
                          <SitePreview
                            url={r.url}
                            alt={r.name}
                            className="w-full h-full"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <p className="text-sm font-bold text-slate-700 group-hover:text-indigo-600 truncate transition-colors">
                              {r.name}
                            </p>
                            {r.verified && (
                              <FiCheckCircle
                                size={11}
                                className="text-indigo-500 flex-shrink-0"
                              />
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <StarRating rating={r.rating} size="xs" />
                            <span className="text-xs font-bold text-slate-600">
                              {r.rating}
                            </span>
                          </div>
                        </div>
                        <FiChevronRight
                          size={13}
                          className="text-slate-300 group-hover:text-indigo-500 transition-colors flex-shrink-0"
                        />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Submit CTA */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                className="card p-5 bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-100"
              >
                <h3 className="text-sm font-extrabold text-slate-900 mb-1">
                  Own a website?
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  Submit it to our directory and get discovered by thousands.
                </p>
                <Link to="/submit" className="btn-primary w-full !py-2">
                  Submit Your Website
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteDetails;
